import { SvgIconComponent } from "@mui/icons-material";
import {
  Badge,
  Box,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  styled,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import React from "react";

import type { ActionSidebarProps } from "../action-sidebar/action-sidebar";
import { Tooltip } from "../tooltip/tooltip";

/** Props for header action buttons */
export type HeaderActionButton = {
  /** MUI icon component to be displayed */
  icon: SvgIconComponent;
  /** Optional text to display next to the icon */
  text?: string;
  /** Badge count to display on the icon */
  badgeValue?: number;
  /** Click handler for the button */
  onClick?: () => void;
  /** Aria label for the button */
  ariaLabel?: string;
  /** Action sidebar support for the button */
  actionSidebar?: ActionSidebarProps;
  /** Dropdown menu support for the button */
  menu?: {
    items: Array<{
      label: string;
      onClick: () => void;
      selectable?: boolean;
    }>;
  };
  /** Optional disabled state for the button */
  disabled?: boolean;
  /** Optional tooltip text for the button */
  tooltip?: string;
};

/** Component for buttons that display both icon and text */
const StyledBox = styled(Box)<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    padding: "8px",
    borderRadius: "8px",
    backgroundColor: disabled ? "#0000001f" : theme.palette.secondary.light,
    cursor: disabled ? "default" : "pointer",
    "&:hover": {
      backgroundColor: disabled ? "#0000001f" : theme.palette.grey[200],
    },
    width: "auto",
    justifyContent: "center",
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
      fontSize: "1.25rem",
    },
  })
);

/** Component for icon-only buttons */
const IconOnlyButton = styled(IconButton)(({ theme, disabled }) => ({
  backgroundColor: disabled
    ? "#0000001f !important"
    : theme.palette.secondary.light,
  padding: "8px",
  borderRadius: "8px",
  minWidth: 40,
  width: "auto",
  "&:hover": {
    backgroundColor: disabled
      ? "#0000001f !important"
      : theme.palette.grey[200],
  },
  "& .MuiSvgIcon-root": {
    color: disabled ? "#00000042 !important" : theme.palette.primary.main,
  },
}));

const StyledBadge = styled(Badge)<{ hasText?: boolean }>(({ hasText }) => ({
  "& .MuiBadge-badge": {
    right: -9,
    top: hasText ? -7 : -4,
    padding: "0 4px",
    fontSize: "12px",
    height: "18px",
    minWidth: "18px",
  },
}));

/** Component for text with badge */
const TextContainer = styled(Box)({
  position: "relative",
});

export const HeaderActionButtonComponent: PimoReactComponent<
  HeaderActionButton
> = ({
  icon: Icon,
  text,
  badgeValue,
  onClick,
  ariaLabel,
  menu,
  disabled,
  tooltip,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    if (menu) {
      setAnchorEl(event.currentTarget);
    } else if (onClick) {
      onClick();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (
    itemOnClick: () => void,
    selectable?: boolean
  ) => {
    if (!selectable) handleClose();
    itemOnClick();
  };

  const ButtonCmp = (
    <>
      {text ? (
        <StyledBox disabled={disabled} onClick={handleClick} {...props}>
          <Icon sx={{ marginRight: 0.5 }} />
          <TextContainer>
            <StyledBadge badgeContent={badgeValue} color="error" hasText>
              <Typography
                variant="body2"
                color={disabled ? "#00000042" : "primary"}
                fontWeight={600}
              >
                {text}
              </Typography>
            </StyledBadge>
          </TextContainer>
        </StyledBox>
      ) : (
        <IconOnlyButton
          disabled={disabled}
          onClick={handleClick}
          aria-label={ariaLabel || text || "action button"}
          {...props}
        >
          <StyledBadge badgeContent={badgeValue} color="error">
            <Icon />
          </StyledBadge>
        </IconOnlyButton>
      )}
      {menu && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {menu.items.map((item, index) => (
            <MenuItem
              key={index}
              sx={item.selectable ? { userSelect: "text" } : undefined}
              onClick={() => handleMenuItemClick(item.onClick, item.selectable)}
            >
              <ListItemText primary={item.label} />
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="bottom">
      {/* Need to add a span wrapper, because tooltips dont show on disabled buttons */}
      <span>{ButtonCmp}</span>
    </Tooltip>
  ) : (
    ButtonCmp
  );
};
